import { breadcrumbScript } from "@/lib/seo-jsonld";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BadgeCheck,
  Clock,
  Crown,
  Heart,
  Mail,
  MapPin,
  MessageCircle,
  Pencil,
  Phone,
  Store as StoreIcon,
  Truck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdCard } from "@/components/ad-card";
import { useAuth } from "@/hooks/use-auth";
import { Stars, StoreReviewsSection } from "@/components/store-reviews";
import { StoreVideosSection } from "@/components/store-videos-section";
import { StoreMediaSection } from "@/components/store-media-section";
import { StoreOrderForm } from "@/components/store-order-form";

import { azError } from "@/lib/az-errors";
import {
  averageRating,
  deliveryLabels,
  fetchFollowerCount,
  fetchStoreBySlug,
  fetchStoreListings,
  fetchStoreReviews,
  isFollowingStore,
  storePlanLabels,
  toggleFollowStore,
} from "@/lib/stores";

export const Route = createFileRoute("/magaza/$slug")({
  loader: async ({ params }) => {
    const store = await fetchStoreBySlug(params.slug);
    if (!store) throw notFound();
    return { store };
  },
  errorComponent: () => (
    <div className="container-x py-24 text-center">
      <h1 className="text-2xl font-extrabold">Mağaza yüklənmədi</h1>
      <p className="mt-2 text-sm text-muted-foreground">Yenidən cəhd edin.</p>
    </div>
  ),
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Mağaza tapılmadı — Albazar.az" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { store } = loaderData;
    const title = `${store.name} — Mağaza | Albazar.az`;
    const description =
      store.description?.slice(0, 155) ||
      `${store.name} mağazasının bütün aktiv elanlarına Albazar.az-da bax və satıcı ilə əlaqə saxla.`;
    const url = `https://albazar.az/magaza/${params.slug}`;
    const image = store.banner_url || store.logo_url || null;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Albazar.az" },
        { property: "og:locale", content: "az_AZ" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(image && /^https:\/\//.test(image)
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbScript([
          { name: "Mağazalar", path: "/magazalar" },
          { name: store.name, path: `/magaza/${params.slug}` },
        ]),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Store",
            name: store.name,
            url,
            ...(store.description ? { description: store.description } : {}),
            ...(store.logo_url ? { image: store.logo_url } : {}),
            ...(store.phone ? { telephone: store.phone } : {}),
            ...(store.city || store.address
              ? {
                  address: {
                    "@type": "PostalAddress",
                    addressCountry: "AZ",
                    ...(store.city ? { addressLocality: store.city } : {}),
                    ...(store.address ? { streetAddress: store.address } : {}),
                  },
                }
              : {}),
          }),
        },
      ],
    };
  },
  component: StorePage,
  notFoundComponent: StoreNotFound,
});

function StoreNotFound() {
  return (
    <div className="container-x py-16 text-center">
      <StoreIcon className="mx-auto size-8 text-muted-foreground" />
      <h1 className="mt-3 text-xl font-extrabold">Mağaza tapılmadı</h1>
      <Link to="/magazalar" className="mt-4 inline-block">
        <Button className="h-10 rounded-full">Bütün mağazalar</Button>
      </Link>
    </div>
  );
}

function StorePage() {
  const { slug } = Route.useParams();
  const { user } = useAuth();
  const qc = useQueryClient();

  const loaderData = Route.useLoaderData();
  const { data: store, isLoading } = useQuery({
    queryKey: ["store", slug],
    queryFn: () => fetchStoreBySlug(slug),
    initialData: loaderData.store,
  });

  const { data: ads } = useQuery({
    queryKey: ["store-ads", store?.owner_id],
    queryFn: () => fetchStoreListings(store!.owner_id!),
    enabled: Boolean(store?.owner_id),
  });

  const { data: followers = 0 } = useQuery({
    queryKey: ["store-followers", store?.id],
    queryFn: () => fetchFollowerCount(store!.id),
    enabled: Boolean(store?.id),
  });

  const { data: following = false } = useQuery({
    queryKey: ["store-following", store?.id, user?.id],
    queryFn: () => isFollowingStore(store!.id, user!.id),
    enabled: Boolean(store?.id && user?.id),
  });

  const { data: reviews } = useQuery({
    queryKey: ["store-reviews", store?.id],
    queryFn: () => fetchStoreReviews(store!.id),
    enabled: Boolean(store?.id),
  });

  const onFollow = async () => {
    if (!user || !store) return;
    try {
      const next = await toggleFollowStore(store.id, user.id, following);
      await qc.invalidateQueries({ queryKey: ["store-following", store.id, user.id] });
      await qc.invalidateQueries({ queryKey: ["store-followers", store.id] });
      toast.success(next ? "Mağazanı izləyirsiniz" : "İzləmə ləğv edildi");
    } catch (err) {
      toast.error(azError(err, "Əməliyyat alınmadı"));
    }
  };

  if (isLoading) {
    return <div className="container-x py-10 text-sm text-muted-foreground">Yüklənir...</div>;
  }

  if (!store) throw notFound();

  const isOwner = Boolean(user && store.owner_id === user.id);
  const avg = averageRating(reviews);
  const isVip = store.plan === "vip";
  const isPremium = store.plan === "premium";

  return (
    <div className="pb-8">
      <div className="relative h-32 w-full bg-gradient-to-br from-primary/30 to-primary/5 sm:h-52">
        {store.banner_url ? (
          <img
            src={store.banner_url}
            alt={`${store.name} banner`}
            className="size-full object-cover"
          />
        ) : null}
      </div>

      <div className="container-x">
        <div className="-mt-10 flex items-end gap-3 sm:-mt-14">
          <div className="size-20 overflow-hidden rounded-2xl border-4 border-background bg-muted shadow-card sm:size-28">
            {store.logo_url ? (
              <img src={store.logo_url} alt={`${store.name} loqo`} className="size-full object-cover" />
            ) : (
              <span className="grid size-full place-items-center text-2xl font-black text-primary">
                {store.name.slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          {isOwner && (
            <Link to="/magazam" className="ml-auto mb-1">
              <Button variant="outline" size="sm" className="rounded-full">
                <Pencil className="size-4" /> Redaktə et
              </Button>
            </Link>
          )}
          {!isOwner && user && (
            <Button
              size="sm"
              variant={following ? "outline" : "default"}
              onClick={() => void onFollow()}
              className="ml-auto mb-1 rounded-full font-semibold"
            >
              <Heart className={`size-4 ${following ? "fill-primary text-primary" : ""}`} />
              {following ? "İzlənilir" : "İzlə"}
            </Button>
          )}
          {!isOwner && !user && (
            <Link to="/giris" className="ml-auto mb-1">
              <Button size="sm" className="rounded-full font-semibold">
                <Heart className="size-4" /> İzlə
              </Button>
            </Link>
          )}
        </div>

        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-extrabold tracking-tight sm:text-2xl">{store.name}</h1>
            {store.verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-info/10 px-2.5 py-1 text-xs font-semibold text-info">
                <BadgeCheck className="size-3.5" /> Təsdiqlənmiş mağaza
              </span>
            )}
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                isVip
                  ? "bg-vip text-vip-foreground shadow-glow"
                  : isPremium
                    ? "bg-premium text-premium-foreground"
                    : "bg-accent"
              }`}
            >
              {(isVip || isPremium) && <Crown className="size-3.5" />}
              {storePlanLabels[store.plan] ?? store.plan} mağaza
            </span>
          </div>
          {store.description && (
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{store.description}</p>
          )}

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="size-3.5" /> {followers} izləyici
            </span>
            <span className="flex items-center gap-1">
              <Stars value={avg} size={13} /> {avg ? avg.toFixed(1) : "—"} ({reviews?.length ?? 0})
            </span>
            {store.city && (
              <span className="flex items-center gap-1">
                <MapPin className="size-3.5" /> {store.city}
                {store.address ? `, ${store.address}` : ""}
              </span>
            )}
            {store.work_hours && (
              <span className="flex items-center gap-1">
                <Clock className="size-3.5" /> {store.work_hours}
              </span>
            )}
            {store.email && (
              <span className="flex items-center gap-1">
                <Mail className="size-3.5" /> {store.email}
              </span>
            )}
            {store.category && (
              <span className="flex items-center gap-1">
                <StoreIcon className="size-3.5" /> {store.category}
              </span>
            )}
            {store.delivery?.length > 0 && (
              <span className="flex items-center gap-1">
                <Truck className="size-3.5" />
                {store.delivery.map((d) => deliveryLabels[d] ?? d).join(", ")}
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {store.phone && (
              <a href={`tel:${store.phone}`}>
                <Button className="h-11 rounded-full font-semibold">
                  <Phone className="size-4" /> Zəng et
                </Button>
              </a>
            )}
            {store.whatsapp && (
              <a
                href={`https://wa.me/${store.whatsapp.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button className="h-11 rounded-full bg-success font-semibold text-success-foreground hover:bg-success/90">
                  <MessageCircle className="size-4" /> WhatsApp
                </Button>
              </a>
            )}
          </div>
        </div>

        <h2 className="mt-7 text-base font-bold sm:text-lg">
          Mağazanın məhsulları {ads ? `(${ads.length})` : ""}
        </h2>
        {ads && ads.length > 0 ? (
          <div className="mt-3 grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-4">
            {ads.map((ad, i) => (
              <AdCard key={ad.id} ad={ad} index={i} />
            ))}
          </div>
        ) : (
          <p className="mt-3 rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Bu mağazada hələ aktiv elan yoxdur.
          </p>
        )}

        <StoreOrderForm
          storeId={store.id}
          ownerId={store.owner_id}
          storeName={store.name}
          productName={ads?.[0]?.title}
          productPrice={ads?.[0]?.price}
          listingId={ads?.[0]?.id}
        />

        <StoreMediaSection storeId={store.id} />


        <StoreVideosSection storeId={store.id} />

        <StoreReviewsSection storeId={store.id} userId={user?.id ?? null} />
      </div>
    </div>
  );
}